import React, { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Collapse, Flex, Space, Spin, Typography } from 'antd';
import type { CollapseProps } from 'antd';
import { isEmpty } from 'lodash';

import { useGetStaticConfig } from '../api/getStaticConfig';
import { IWebhookConfig } from '../types';
import { AddWebhookModal } from './AddWebhookModal';
import { EditWebhookModal } from './EditWebhookModal';
import { WebhookAuthTag } from './WebhookAuthTag';
import { WebhookUrlItem } from './WebhookUrlItem';
import { WebhookUrlItemActions } from './WebhookUrlItemActions';
import { useGetWebhookConfigs } from '../api/getWebhookConfigs';
import { scopes } from '@/config';
import useAppState from '@/hooks/useAppState';

interface IWebhookUrlsSectionProps {
  partnerId: string;
}

export const WebhookUrlsSection = ({ partnerId }: IWebhookUrlsSectionProps) => {
  const { data: configs, isLoading: configsLoading } =
    useGetWebhookConfigs(partnerId);
  const { data: staticConfigData, isLoading: staticLoading } =
    useGetStaticConfig();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<IWebhookConfig | null>(
    null
  );
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const staticConfig = staticConfigData?.static_config ?? [];
  const names = staticConfigData?.names ?? {};
  const isLoading = configsLoading || staticLoading;
  const userScopes = useAppState((state) => state.scopes);
  const canManage = userScopes.includes(scopes.MANAGE_WEBHOOKS);

  const collapseItems: CollapseProps['items'] = (configs ?? []).map(
    (config) => ({
      key: config.config_id,
      label: (
        <Space>
          <Typography.Text copyable>{config.url}</Typography.Text>
          <WebhookAuthTag authType={config.auth_type} />
        </Space>
      ),
      extra: (
        <WebhookUrlItemActions
          config={config}
          partnerId={partnerId}
          onEdit={setEditingConfig}
        />
      ),
      children: (
        <WebhookUrlItem
          config={config}
          partnerId={partnerId}
          staticConfig={staticConfig}
          names={names}
        />
      ),
    })
  );

  return (
    <Card title="Webhook URLs" data-testid="webhook-urls-section">
      {isLoading ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : isEmpty(collapseItems) ? (
        <Space direction="vertical" style={{ marginBottom: '1rem' }}>
          <Typography.Text>No webhook URLs available</Typography.Text>
        </Space>
      ) : (
        <Collapse
          items={collapseItems}
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
          style={{ marginBottom: 8 }}
        />
      )}

      {canManage && (
        <Button
          type="link"
          icon={<PlusOutlined />}
          onClick={() => setAddModalOpen(true)}
          data-testid="webhook-add-url-button"
        >
          Add new URL
        </Button>
      )}

      <AddWebhookModal
        partnerId={partnerId}
        open={addModalOpen}
        staticConfig={staticConfig}
        onClose={() => setAddModalOpen(false)}
        onSuccess={(configId) => setActiveKeys((prev) => [...prev, configId])}
      />

      {editingConfig && (
        <EditWebhookModal
          partnerId={partnerId}
          config={editingConfig}
          open
          onClose={() => setEditingConfig(null)}
        />
      )}
    </Card>
  );
};
